import { createFileRoute, Link } from '@tanstack/react-router';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { listJobQueue } from '@mkcore/platform/schema';
import { EmptyState, ErrorState, Mono, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { JobStatusBadge } from '@/features/admin/job-status';

export const Route = createFileRoute('/_app/admin/jobs/queue')({ component: JobQueue });

const queueQuery = queryOptions({ queryKey: ['admin', 'job', 'queue'], queryFn: () => call(listJobQueue, {}), refetchInterval: 5000 });

/** The queues with their limits and the runs that wait on them (CO-0721, CO-0718). */
function JobQueue() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const q = useQuery(queueQuery);
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-64" />;
  return (
    <>
      <Link to="/admin/jobs" className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {t('jobs.title')}
      </Link>
      <PageHeader eyebrow={t('nav.jobs')} title={t('queue.title')} description={t('queue.description')} />
      <Table>
        <THead>
          <Tr>
            <Th>{t('queue.name')}</Th>
            <Th className="text-right">{t('queue.limit')}</Th>
            <Th className="text-right">{t('queue.perKey')}</Th>
            <Th className="text-right">{t('queue.waiting')}</Th>
          </Tr>
        </THead>
        <TBody>
          {q.data.queues.map((qu) => (
            <Tr key={qu.name}>
              <Td>
                <Mono className="text-xs">{qu.name}</Mono>
              </Td>
              <Td className="text-right tabular">{qu.concurrency ?? '—'}</Td>
              <Td className="text-right tabular">{qu.partitionConcurrency ?? '—'}</Td>
              <Td className="text-right tabular">{qu.waiting}</Td>
            </Tr>
          ))}
        </TBody>
      </Table>
      <div className="mt-6">
        {!q.data.items.length ? (
          <EmptyState title={t('queue.empty')} />
        ) : (
          <Table>
            <THead>
              <Tr>
                <Th>{t('jobs.type')}</Th>
                <Th>{t('queue.name')}</Th>
                <Th>{t('queue.key')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('common.created')}</Th>
              </Tr>
            </THead>
            <TBody>
              {q.data.items.map((i) => (
                <Tr key={i.workflowId}>
                  <Td>
                    <Link to="/admin/jobs/$id" params={{ id: i.workflowId }} className="font-bold hover:underline">
                      {i.jobType}
                    </Link>
                    <div>
                      <Mono className="text-[11px]">{i.workflowId}</Mono>
                    </div>
                  </Td>
                  <Td>
                    <Mono className="text-xs">{i.queueName ?? '—'}</Mono>
                  </Td>
                  <Td>
                    <Mono className="text-xs">{i.partitionKey ?? '—'}</Mono>
                  </Td>
                  <Td>
                    <JobStatusBadge status={i.status as 'waiting'} />
                  </Td>
                  <Td className="whitespace-nowrap tabular text-fg-2">{formatDateTime(i.createdAt)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </>
  );
}
